import { Chip, ChipBackgroundColor, ChipSize } from "@amzn/stencil-react-components/chip";
import { Row, View } from "@amzn/stencil-react-components/layout";
import { Text, TextVariant } from "@amzn/stencil-react-components/text";
import { TooltipAlignment, withTooltip } from "@amzn/stencil-react-components/tooltip";
import { IconInformationFillExtraSmall } from "@amzn/stencil-react-icons";
import React, { FC } from "react";

export enum ReviewType {
  USER = "User review",
  AI = "AI review",
}

export interface ProductReviewCardProps {
  categories: string[];
  content: string;
  title: string;
  dataTestId?: string;
  isSelf?: boolean;
  jobFamily?: string;
  reviewType?: ReviewType;
  votes?: number;
}

export const ProductReviewCard: FC<ProductReviewCardProps> = ({
  title,
  categories,
  votes,
  isSelf = false,
  content,
  dataTestId,
  reviewType,
  jobFamily,
}) => {
  const JobFamilyChip = () => (
    <Chip
      backgroundColor={ChipBackgroundColor.Purple}
      size={ChipSize.ExtraSmall}
      dataTestId="product-review-chip-job-family"
    >
      <Row alignItems="center" gridGap={"dimensions.spacing.50"}>
        <IconInformationFillExtraSmall /> <Text>{`${jobFamily} feedback`}</Text>
      </Row>
    </Chip>
  );

  const SelfReviewChip = () => (
    <Chip backgroundColor={ChipBackgroundColor.Purple} size={ChipSize.ExtraSmall} dataTestId="product-review-chip-self">
      <Text>Your review</Text>
    </Chip>
  );

  const renderReviewTypeChip = () => {
    if (reviewType) {
      return (
        <Chip backgroundColor={ChipBackgroundColor.Blue} size={ChipSize.ExtraSmall} dataTestId="product-review-chip">
          <Text variant={TextVariant.BodySMInteractive}>{reviewType}</Text>
        </Chip>
      );
    }
  };

  const renderChips = () => {
    const chips: JSX.Element[] = [];
    if (isSelf) {
      chips.push(<SelfReviewChip />);
    }
    if (jobFamily) {
      const JobFamilyChipWithTooltip = withTooltip()(JobFamilyChip);
      chips.push(
        <JobFamilyChipWithTooltip
          tooltipText="The author of this review belongs to this job family."
          tooltipAlignment={TooltipAlignment.Left}
        />
      );
    }

    categories.forEach((category) => {
      chips.push(
        <Chip
          backgroundColor={ChipBackgroundColor.Blue}
          size={ChipSize.ExtraSmall}
          dataTestId={`product-review-chip-category-${category}`}
        >
          <Text>{category}</Text>
        </Chip>
      );
    });
    return chips;
  };

  return (
    <View style={{ marginTop: "16px", marginBottom: "8px" }} dataTestId={dataTestId ?? "product-review-card"}>
      {renderReviewTypeChip()}
      <Row justifyContent="space-between" style={{ marginTop: "16px", marginBottom: "8px", wordBreak: "break-word" }}>
        <Text variant={TextVariant.HeadingSM} dataTestId="product-review-title">
          {title.charAt(0).toUpperCase() + title.slice(1)}
        </Text>
        {/* DISABLED UNTIL EVERYTHING IS ENABLED */}
        {/* <Row alignItems="center">
          <IconThumbUpExtraSmall />
          <Text>{votes}</Text>
        </Row> */}
      </Row>
      <Text style={{ wordBreak: "break-word" }} dataTestId="product-review-content">
        {content.charAt(0).toUpperCase() + content.slice(1)}
      </Text>
      <Row
        flexWrap="wrap"
        rowGap="dimensions.spacing.200"
        columnGap="dimensions.spacing.200"
        padding={["dimensions.spacing.250", 0, "dimensions.spacing.250", 0]}
      >
        {renderChips()}
      </Row>
    </View>
  );
};
